import type { PurchaseSummary } from '@src/common/types/purchaseSummary';
import type { Cart } from '@src/domains/cart';
import {
  SUMMARY_SOURCE,
  isCartSummaryPresentation,
  isDirectLinkPaymentPresentation,
  isSaleSummaryPresentation,
  type SummarySource,
  type Transactable,
} from '@src/domains/transactables';
import type { UseCartSummaryResult } from '@src/features/cart';
import type { UseOrderSummaryResult } from '@src/features/orders';
import type { UseSaleChangeSummaryResult } from '@src/features/sale-change/summary';
import type { UseSaleSummaryResult } from '@src/features/sales/summary';

import type { SummaryLineItem } from './summaryLineItem';

export type CompactSummaryInput = {
  source: SummarySource;
  transactable: Transactable;
  cart?: Cart;
  cartSummary: UseCartSummaryResult;
  orderSummary: UseOrderSummaryResult;
  saleSummary: UseSaleSummaryResult;
  saleChangeSummary: UseSaleChangeSummaryResult;
  purchaseSummary: PurchaseSummary;
};

type CompactSummary = {
  totalAmountCents: number;
  productCount: number;
};

export type CompactSummaryResult = {
  compactSummary: CompactSummary;
  compactItems: SummaryLineItem[];
};

export const resolveCompactSummary = (input: CompactSummaryInput): CompactSummaryResult => {
  const { source, transactable, cart, cartSummary, orderSummary, saleSummary, saleChangeSummary, purchaseSummary } =
    input;

  switch (source) {
    case SUMMARY_SOURCE.SALE_CHANGE:
      return {
        compactSummary: saleChangeSummary,
        compactItems: saleChangeSummary.items,
      };

    case SUMMARY_SOURCE.ORDER:
      return {
        compactSummary: orderSummary,
        compactItems: orderSummary.items,
      };

    case SUMMARY_SOURCE.DIRECT_LINK:
      if (isCartSummaryPresentation(source, transactable)) {
        return {
          compactSummary: cartSummary,
          compactItems: cart?.items ?? [],
        };
      }

      if (isSaleSummaryPresentation(source, transactable)) {
        return {
          compactSummary: saleSummary,
          compactItems: saleSummary.items,
        };
      }

      if (isDirectLinkPaymentPresentation(source, transactable)) {
        return {
          compactSummary: {
            totalAmountCents: transactable.directLink?.totalAmountCurrencyInfo?.amountCents ?? 0,
            productCount: 0,
          },
          compactItems: [],
        };
      }

      break;

    case SUMMARY_SOURCE.SALE:
      return {
        compactSummary: saleSummary,
        compactItems: saleSummary.items,
      };

    case SUMMARY_SOURCE.CART:
      return {
        compactSummary: cartSummary,
        compactItems: cart?.items ?? [],
      };
  }

  return {
    compactSummary: {
      totalAmountCents: purchaseSummary.totalAmountCents,
      productCount: purchaseSummary.items.length,
    },
    compactItems: purchaseSummary.items,
  };
};
